import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useProductAuth } from '../auth/ProductAuthProvider';
import {
  BillingConfig,
  BillingPlan,
  BillingStatus,
  billingApiClient,
} from './BillingApiClient';

type BillingPhase = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

interface ProductBillingContextValue {
  phase: BillingPhase;
  config: BillingConfig | null;
  plans: BillingPlan[];
  status: BillingStatus | null;
  error: string | null;
  refresh: () => Promise<BillingStatus | null>;
  startCheckout: (planSlug: string) => Promise<void>;
  openPortal: () => Promise<void>;
}

const ProductBillingContext = createContext<ProductBillingContextValue | null>(null);

export function ProductBillingProvider({ children }: { children: React.ReactNode }) {
  const auth = useProductAuth();
  const [phase, setPhase] = useState<BillingPhase>('idle');
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const bootstrap = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const nextConfig = await billingApiClient.getConfig();
      setConfig(nextConfig);
      if (!nextConfig.configured) {
        setPlans([]);
        setPhase('unavailable');
        return;
      }
      setPlans(await billingApiClient.listPlans());
      setPhase('ready');
    } catch (reason) {
      setConfig(null);
      setPlans([]);
      setError(reason instanceof Error ? reason.message : 'Tarife konnten nicht geladen werden.');
      setPhase('unavailable');
    }
  }, []);

  const refresh = useCallback(async (): Promise<BillingStatus | null> => {
    if (auth.phase !== 'authenticated') {
      setStatus(null);
      return null;
    }
    setPhase('loading');
    setError(null);
    try {
      const next = await billingApiClient.getStatus();
      setStatus(next);
      setConfig({ configured: next.configured, billing_mode: next.billing_mode });
      setPhase(next.configured ? 'ready' : 'unavailable');
      return next;
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Planstatus konnte nicht geladen werden.';
      setError(message);
      setPhase(message.includes('503') || message.includes('konfiguriert') ? 'unavailable' : 'error');
      return null;
    }
  }, [auth.phase]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (auth.phase === 'authenticated') void refresh();
    if (auth.phase === 'guest') {
      setStatus(null);
    }
  }, [auth.phase, refresh]);

  const startCheckout = useCallback(async (planSlug: string) => {
    setPhase('loading');
    setError(null);
    try {
      const checkout = await billingApiClient.createCheckout(planSlug);
      if (checkout.status !== 'redirect' || !checkout.url.startsWith('https://checkout.stripe.com/')) {
        throw new Error('Ungültige Antwort des Zahlungsdienstes.');
      }
      window.location.assign(checkout.url);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Checkout konnte nicht gestartet werden.';
      setError(message);
      setPhase(message.includes('503') || message.includes('konfiguriert') ? 'unavailable' : 'error');
      throw reason;
    }
  }, []);

  const openPortal = useCallback(async () => {
    setError(null);
    try {
      const portal = await billingApiClient.createPortal();
      if (!portal.url.startsWith('https://billing.stripe.com/')) throw new Error('Ungültige Portal-URL.');
      window.location.assign(portal.url);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Kundenportal konnte nicht geöffnet werden.');
      throw reason;
    }
  }, []);

  const value = useMemo<ProductBillingContextValue>(() => ({
    phase,
    config,
    plans,
    status,
    error,
    refresh,
    startCheckout,
    openPortal,
  }), [config, error, openPortal, phase, plans, refresh, startCheckout, status]);

  return <ProductBillingContext.Provider value={value}>{children}</ProductBillingContext.Provider>;
}

export function useProductBilling(): ProductBillingContextValue {
  const value = useContext(ProductBillingContext);
  if (!value) throw new Error('useProductBilling must be used inside ProductBillingProvider.');
  return value;
}
